import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../lib/utils';

type TimelineProps = Omit<ComponentProps<'div'>, 'aria-label' | 'aria-labelledby'> & {
    'aria-label': string;
};

export function Timeline({ className, ...props }: TimelineProps) {
    return (
        <div
            role="list"
            data-slot="timeline"
            className={cn(
                'grid w-full grid-cols-[auto_auto_minmax(0,1fr)] gap-x-4 text-sm leading-5',
                className,
            )}
            {...props}
        />
    );
}

type TimelineItemProps = Omit<ComponentProps<'div'>, 'children'> & {
    timestamp: ReactNode;
    children: ReactNode;
};

export function TimelineItem({ className, timestamp, children, ...props }: TimelineItemProps) {
    return (
        <div
            role="listitem"
            data-slot="timeline-item"
            className={cn(
                'group/timeline-item col-span-3 grid grid-cols-subgrid pb-3 last:pb-0',
                className,
            )}
            {...props}
        >
            <div data-slot="timeline-timestamp">{timestamp}</div>
            <div aria-hidden className="relative">
                <span className="mt-1.5 block size-2 rounded-full bg-muted-foreground" />
                <span className="absolute -top-1 left-1/2 hidden h-2.5 w-px -ml-[0.5px] bg-[repeating-linear-gradient(to_bottom,var(--color-muted-foreground)_0_4px,transparent_4px_6px)] group-first/timeline-item:block" />
                <span className="absolute -bottom-4.5 left-1/2 top-3.5 w-px -ml-[0.5px] bg-[repeating-linear-gradient(to_bottom,var(--color-muted-foreground)_0_4px,transparent_4px_6px)] group-last/timeline-item:hidden" />
            </div>
            <div data-slot="timeline-content" className="min-w-0">
                {children}
            </div>
        </div>
    );
}

type TimelineTimestampProps = {
    primary: string;
    secondary: string;
};

export function TimelineTimestamp({ primary, secondary }: TimelineTimestampProps) {
    return (
        <>
            <p>{primary}</p>
            <p className="text-muted-foreground">{secondary}</p>
        </>
    );
}
